import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Image, Trophy, MessageSquare, Mail,
  Settings, LogOut, Menu, X, ExternalLink
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exam-excellence-hub/artifacts/e4f9i88p_trident%20logo.jpeg";

const navItems = [
  { path: "/admin/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
  { path: "/admin/gallery", icon: <Image size={18} />, label: "Gallery" },
  { path: "/admin/toppers", icon: <Trophy size={18} />, label: "Toppers" },
  { path: "/admin/testimonials", icon: <MessageSquare size={18} />, label: "Testimonials" },
  { path: "/admin/enquiries", icon: <Mail size={18} />, label: "Enquiries" },
  { path: "/admin/content", icon: <Settings size={18} />, label: "Website Content" },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const isActive = (path) => location.pathname === path;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Trident" className="h-10 w-10 rounded-lg object-contain bg-white p-0.5" />
          <div>
            <div className="text-white font-bold text-xs tracking-widest" style={{ fontFamily: "Manrope, sans-serif" }}>TRIDENT ACADEMY</div>
            <div className="text-xs" style={{ color: "#F2C94C" }}>Admin Panel</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setSidebarOpen(false)}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(" ", "-")}`}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              isActive(item.path)
                ? "text-[#0A2540]"
                : "text-gray-300 hover:text-white hover:bg-white/10"
            }`}
            style={isActive(item.path) ? { backgroundColor: "#F2C94C" } : {}}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10 space-y-2">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/10 transition-all"
        >
          <ExternalLink size={18} />
          View Website
        </a>
        <button
          onClick={logout}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-300 hover:text-white hover:bg-red-500/20 transition-all"
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100 font-[Inter,sans-serif]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col flex-shrink-0" style={{ backgroundColor: "#0A2540" }}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 flex flex-col" style={{ backgroundColor: "#0A2540" }}>
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <h1 className="font-bold text-lg" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
              {navItems.find((n) => isActive(n.path))?.label || "Admin Panel"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">
              {localStorage.getItem("admin_user") || "Admin"}
            </span>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "#0A2540" }}>
              A
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
